/** Client-side crop + rotate. `react-easy-crop` reports a pixel crop box against
 *  the (optionally rotated) source; we render that box onto a canvas and hand the
 *  caller a `File` to feed `api.replace(id, file)`. The source is loaded from the
 *  image's *original* URL so we crop full resolution, not a thumbnail.
 *
 *  Note: cropping a cross-origin image taints the canvas, so `toBlob` would throw.
 *  We request the source with `crossOrigin="anonymous"`; the resize/original host
 *  must send permissive CORS (handled at the CDN, out of scope for this file). */
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';

interface Props {
  open: boolean;
  /** Original-resolution source URL. */
  src: string | null;
  /** Suggested filename for the produced File. */
  filename: string;
  onCancel: () => void;
  onCropped: (file: File) => void;
}

/** Load an <img> for canvas drawing (CORS-enabled so toBlob doesn't taint). */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.src = src;
  });
}

/** Render the crop box (accounting for rotation) to a canvas → Blob → File. */
async function cropToFile(src: string, crop: Area, rotation: number, filename: string): Promise<File> {
  const image = await loadImage(src);
  const rad = (rotation * Math.PI) / 180;

  // Draw the whole (rotated) image onto a scratch canvas sized to its rotated
  // bounding box, then copy just the crop rectangle out into the output canvas.
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const bboxW = image.width * cos + image.height * sin;
  const bboxH = image.width * sin + image.height * cos;

  const scratch = document.createElement('canvas');
  scratch.width = Math.ceil(bboxW);
  scratch.height = Math.ceil(bboxH);
  const sctx = scratch.getContext('2d');
  if (!sctx) throw new Error('Canvas 2D context unavailable');
  sctx.translate(bboxW / 2, bboxH / 2);
  sctx.rotate(rad);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(crop.width));
  out.height = Math.max(1, Math.round(crop.height));
  const octx = out.getContext('2d');
  if (!octx) throw new Error('Canvas 2D context unavailable');
  octx.drawImage(
    scratch,
    Math.round(crop.x),
    Math.round(crop.y),
    out.width,
    out.height,
    0,
    0,
    out.width,
    out.height,
  );

  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) throw new Error('Could not export cropped image');
  const name = filename.replace(/\.[^./\\]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg' });
}

export function ImageCropDialog({ open, src, filename, onCancel, onCropped }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onComplete = useCallback((_area: Area, areaPixels: Area) => {
    setPixels(areaPixels);
  }, []);

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setPixels(null);
    setError(null);
    setBusy(false);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleApply = async () => {
    if (!src || !pixels) return;
    setBusy(true);
    setError(null);
    try {
      const file = await cropToFile(src, pixels, rotation, filename);
      reset();
      onCropped(file);
    } catch {
      setError('Could not process the image. The source may not allow cross-origin editing.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Crop &amp; rotate</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', height: 320, bgcolor: 'grey.900', borderRadius: 1 }}>
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={4 / 3}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onComplete}
              restrictPosition={false}
            />
          )}
        </Box>

        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={4}
              step={0.05}
              onChange={(_e, v) => setZoom(Array.isArray(v) ? (v[0] ?? 1) : v)}
              aria-label="Zoom"
            />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
              Rotate
            </Typography>
            <Tooltip title="Rotate left">
              <IconButton size="small" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} aria-label="Rotate left">
                <RotateLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rotate right">
              <IconButton size="small" onClick={() => setRotation((r) => (r + 90) % 360)} aria-label="Rotate right">
                <RotateRightIcon />
              </IconButton>
            </Tooltip>
            <Slider
              value={rotation}
              min={0}
              max={359}
              step={1}
              onChange={(_e, v) => setRotation(Array.isArray(v) ? (v[0] ?? 0) : v)}
              aria-label="Rotation angle"
            />
          </Stack>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void handleApply()} variant="contained" disabled={busy || !pixels || !src}>
          {busy ? 'Processing…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
